import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Layout Components
import Layout from './components/Layout';
import Login from './components/Login';

// Dashboard
import Dashboard from './components/Dashboard';

// User Management
import UserManagement from './components/UserManagement';
import UserRoles from './components/UserRoles';
import UserPermissions from './components/UserPermissions';

// Inventory Management
import StockManagement from './components/StockManagement';
import StockCard from './components/StockCard';
import StockMovement from './components/StockMovement';
import WarehouseManagement from './components/WarehouseManagement';
import ShelfManagement from './components/ShelfManagement';
import StockCounting from './components/StockCounting';
import ProductManagement from './components/ProductManagement';
import PickListManagement from './components/PickListManagement';

// Sales & Purchase
import InvoiceManagement from './components/InvoiceManagement';
import OrderManagement from './components/OrderManagement';
import OrderToInvoice from './components/OrderToInvoice';
import WaybillManagement from './components/WaybillManagement';
import PurchaseManagement from './components/PurchaseManagement';

// Work Orders
import WorkOrderManagement from './components/WorkOrderManagement';
import WorkOrderToInvoice from './components/WorkOrderToInvoice';
import WorkOrderTypes from './components/WorkOrderTypes';

// System Settings
import SystemSettings from './components/SystemSettings';
import CompanySettings from './components/CompanySettings';
import IntegrationSettings from './components/IntegrationSettings';
import PrintTemplates from './components/PrintTemplates';

// B2B/B2C Management
import B2BManagement from './components/B2BManagement';
import B2CManagement from './components/B2CManagement';
import CampaignManagement from './components/CampaignManagement';
import CouponManagement from './components/CouponManagement';

// Production
import ProductionManagement from './components/ProductionManagement';
import ProductionRecipe from './components/ProductionRecipe';
import ProductionTracking from './components/ProductionTracking';
import WorkStationManagement from './components/WorkStationManagement';
import QualityControl from './components/QualityControl';

// Reports
import Reports from './components/Reports';
import SalesReports from './components/SalesReports';
import InventoryReports from './components/InventoryReports';
import FinancialReports from './components/FinancialReports';

// Consultant
import ConsultantPortal from './components/ConsultantPortal';
import VehicleInspection from './components/VehicleInspection';
import OBDIntegration from './components/OBDIntegration';

// Warehouse Terminal
import WarehouseTerminal from './components/WarehouseTerminal';
import PickingManagement from './components/PickingManagement';
import LocationTransfer from './components/LocationTransfer';

// Customer Relations
import CustomerRelations from './components/CustomerRelations';
import CustomerSurveys from './components/CustomerSurveys';
import ServiceReminders from './components/ServiceReminders';

// HR Management
import HRManagement from './components/HRManagement';
import EmployeeManagement from './components/EmployeeManagement';
import AttendanceTracking from './components/AttendanceTracking';
import LeaveManagement from './components/LeaveManagement';

// Payment & Collection
import PaymentManagement from './components/PaymentManagement';
import CollectionManagement from './components/CollectionManagement';
import BankTransferManagement from './components/BankTransferManagement';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { CompanyProvider } from './context/CompanyContext';

function App() {
  return (
    <AuthProvider>
      <CompanyProvider>
        <Router basename="/">
          <AppContent />
        </Router>
      </CompanyProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<Dashboard />} />
        
        {/* User Management */}
        <Route path="/users" element={<UserManagement />} />
        <Route path="/user-roles" element={<UserRoles />} />
        <Route path="/user-permissions" element={<UserPermissions />} />
        
        {/* Inventory Management */}
        <Route path="/stock" element={<StockManagement />} />
        <Route path="/stock-card" element={<StockCard />} />
        <Route path="/stock-movement" element={<StockMovement />} />
        <Route path="/warehouse" element={<WarehouseManagement />} />
        <Route path="/shelf" element={<ShelfManagement />} />
        <Route path="/stock-counting" element={<StockCounting />} />
        <Route path="/products" element={<ProductManagement />} />
        <Route path="/pick-lists" element={<PickListManagement />} />
        
        {/* Sales & Purchase */}
        <Route path="/invoices" element={<InvoiceManagement />} />
        <Route path="/orders" element={<OrderManagement />} />
        <Route path="/order-to-invoice" element={<OrderToInvoice />} />
        <Route path="/waybills" element={<WaybillManagement />} />
        <Route path="/purchases" element={<PurchaseManagement />} />
        
        {/* Work Orders */}
        <Route path="/work-orders" element={<WorkOrderManagement />} />
        <Route path="/work-order-to-invoice" element={<WorkOrderToInvoice />} />
        <Route path="/work-order-types" element={<WorkOrderTypes />} />
        
        {/* System Settings */}
        <Route path="/settings" element={<SystemSettings />} />
        <Route path="/company-settings" element={<CompanySettings />} />
        <Route path="/integration-settings" element={<IntegrationSettings />} />
        <Route path="/print-templates" element={<PrintTemplates />} />
        
        {/* B2B/B2C Management */}
        <Route path="/b2b" element={<B2BManagement />} />
        <Route path="/b2c" element={<B2CManagement />} />
        <Route path="/campaigns" element={<CampaignManagement />} />
        <Route path="/coupons" element={<CouponManagement />} />
        
        {/* Production */}
        <Route path="/production" element={<ProductionManagement />} />
        <Route path="/production-recipe" element={<ProductionRecipe />} />
        <Route path="/production-tracking" element={<ProductionTracking />} />
        <Route path="/work-stations" element={<WorkStationManagement />} />
        <Route path="/quality-control" element={<QualityControl />} />
        
        {/* Reports */}
        <Route path="/reports" element={<Reports />} />
        <Route path="/sales-reports" element={<SalesReports />} />
        <Route path="/inventory-reports" element={<InventoryReports />} />
        <Route path="/financial-reports" element={<FinancialReports />} />
        
        {/* Consultant */}
        <Route path="/consultant" element={<ConsultantPortal />} />
        <Route path="/vehicle-inspection" element={<VehicleInspection />} />
        <Route path="/obd-integration" element={<OBDIntegration />} />
        
        {/* Warehouse Terminal */}
        <Route path="/warehouse-terminal" element={<WarehouseTerminal />} />
        <Route path="/picking" element={<PickingManagement />} />
        <Route path="/location-transfer" element={<LocationTransfer />} />
        
        {/* Customer Relations */}
        <Route path="/customer-relations" element={<CustomerRelations />} />
        <Route path="/customer-surveys" element={<CustomerSurveys />} />
        <Route path="/service-reminders" element={<ServiceReminders />} />
        
        {/* HR Management */}
        <Route path="/hr" element={<HRManagement />} />
        <Route path="/employees" element={<EmployeeManagement />} />
        <Route path="/attendance" element={<AttendanceTracking />} />
        <Route path="/leave-management" element={<LeaveManagement />} />
        
        {/* Payment & Collection */}
        <Route path="/payments" element={<PaymentManagement />} />
        <Route path="/collections" element={<CollectionManagement />} />
        <Route path="/bank-transfers" element={<BankTransferManagement />} />
      </Routes>
    </Layout>
  );
}

export default App; 